<script lang="ts">
  import { onMount } from 'svelte';
  import Fa, { FaLayers, FaLayersText } from 'svelte-fa/src';
  import {
    faCirclePlay,
    faCube,
    faFileArrowUp,
    faHandPointer,
    faLayerGroup,
    faSpinner,
  } from '@fortawesome/free-solid-svg-icons';
  import Alert from 'sveltestrap/src/Alert.svelte';
  import Tooltip from 'sveltestrap/src/Tooltip.svelte';
  import type { VViewerOptions } from '../lib';
  import { MATERIAL_CHANNELS, VViewer, version } from '../lib';

  export let libraryUrls = {
    BABYLON: 'https://cdn.jsdelivr.net/npm/babylonjs@6.7.0/babylon.min.js',
    LOADERS: 'https://cdn.jsdelivr.net/npm/babylonjs-loaders@6.7.0/babylonjs.loaders.min.js',
    MATERIALS: 'https://cdn.jsdelivr.net/npm/babylonjs-materials@6.7.0/babylonjs.materials.min.js',
  };
  export let colorScheme: 'light' | 'dark' = 'light';
  export let bigButtonEnabled = false;
  export let viewerHintEnabled = false;
  export let autoStart = false;
  export let attributionMessage: string | undefined = undefined;
  export let thumbnailBackgroundColor: string | undefined = undefined;
  export let thumbnailUrl: string | undefined = undefined;
  export let meshUrl: string | undefined = undefined;
  export let usdzUrl: string | undefined = undefined;
  export let environmentTextureOptions: { label: string; value: string; selected: boolean }[] = [];
  export let highDefinition = false;
  export let sceneClearColor = '#00000000';
  export let cameraRadius = 1;
  export let cameraAlpha = Math.PI * (5 / 8);
  export let cameraBeta = Math.PI * (3 / 8);
  export let cameraLowerAlphaLimit: number | null = null;
  export let cameraUpperAlphaLimit: number | null = null;
  export let cameraLowerBetaLimit: number | null = 0;
  export let cameraUpperBetaLimit: number | null = Math.PI;
  export let cameraAutoRotation = false;
  export let lightIntensity = 0;
  export let groundEnabled = true;
  export let groundSize = 1;
  export let groundColor = '#999999';
  export let groundOpacity = 0.5;
  export let groundMirrorEnabled = false;
  export let skyboxEnabled = false;
  export let gridEnabled = false;
  export let controlsEnabled = true;
  export let brandEnabled = true;
  export let inspectorEnabled = true;
  export let inspectorOpened = false;
  export let versionVisibility = true;
  export let channelsEnabled = true;
  export let filesInputEnabled = false;
  export let filesInputMessage = 'Drag and drop 3D model files to view them';
  export let arEnabled = true;
  export let debugLayer = false;
  export let errorMessagesVisibility = true;

  let canvasRef: HTMLCanvasElement;
  let fileRef: HTMLInputElement;
  let errorMessages: string[] = [];
  let cameraActionHintOpacity = 1;
  let vviewer: VViewer | undefined;
  let loading = false;
  let loadingMessage = '';
  let selectedEnvironmentTextureOption =
    environmentTextureOptions.find(({ selected }) => selected) || environmentTextureOptions[0];
  let skyboxDisabled = !skyboxEnabled;

  const humanize = (str: string) =>
    str
      .replace(/([a-z0-9])([A-Z])/g, (_x, y, z) => `${y} ${z.toLocaleLowerCase()}`)
      .replace(/^[a-z]/, (x) => x.toLocaleUpperCase());

  const environmentOptions = {
    createGround: groundEnabled,
    groundSize,
    groundColor,
    groundOpacity,
    enableGroundShadow: false,
    enableGroundMirror: groundMirrorEnabled,
    groundMirrorBlurKernel: 32,
    groundMirrorAmount: 2,
    createSkybox: false,
  };

  const skyboxDefaultOptions = {
    pbr: true,
    scale: 10000,
    blur: 0.15,
    setGlobalEnvTexture: true,
  };
  const gridOptions = {
    width: 1,
    height: 0,
    subdivisions: 1,
    majorUnitFrequency: 5,
    minorUnitVisibility: 0.25,
    gridRatio: 0.02,
    backFaceCulling: false,
    mainColor: '#ffffff',
    lineColor: '#ffffff',
    opacity: 0.5,
    zOffset: 1.0,
  };
  const filesInputOptions = {};

  const materialChannels = Object.keys(MATERIAL_CHANNELS) as (keyof typeof MATERIAL_CHANNELS)[];
  let channel = materialChannels[0];
  let arView: InstanceType<typeof VViewer>['arView'];

  $: finalRender = !channel || channel === 'finalRender';

  const prepare = async () => {
    loading = true;
    vviewer?.dispose();
    const vv = await VViewer.create(
      canvasRef,
      {
        engine: {
          onResize: highDefinition
            ? (engine) => {
                const scale = Math.max(window.innerWidth, window.innerHeight) / 1920;
                const minScaleLevel = 1 / (1 + (window.devicePixelRatio + 1));
                engine.setHardwareScalingLevel(Math.min(1, Math.max(scale, minScaleLevel)));
              }
            : undefined,
        },
        scene: {
          clearColor: finalRender ? (!skyboxDisabled ? '#ffffff' : sceneClearColor) : '#d9d9d9',
          environmentTexture: selectedEnvironmentTextureOption?.value,
          debugLayerVisiblity: debugLayer,
        },
        camera: {
          radius: cameraRadius,
          alpha: cameraAlpha,
          beta: cameraBeta,
          lowerAlphaLimit: cameraLowerAlphaLimit,
          upperAlphaLimit: cameraUpperAlphaLimit,
          lowerBetaLimit: cameraLowerBetaLimit,
          upperBetaLimit: cameraUpperBetaLimit,
          useFramingBehavior: true,
          useAutoRotationBehavior: cameraAutoRotation,
        },
        light: {
          specular: '#000000',
          groundColor: '#808080',
          intensity: lightIntensity,
        },
        environment: finalRender ? environmentOptions : null,
        skybox: !skyboxDisabled && finalRender ? skyboxDefaultOptions : null,
        grid: gridEnabled ? gridOptions : null,
        defaultPipeline: {
          imageProcessingEnabled: finalRender,
          imageProcessingExposure: 1.5,
          samples: 1,
          fxaaEnabled: true,
          sharpenEnabled: true,
          sharpenEdgeAmount: 0.2,
        },
        meshRendering: {
          pbrMaterial: {
            subSurface: {
              indexOfRefraction: 1.5,
              volumeIndexOfRefraction: 1.5,
              refractionTexture: null,
            },
          },
          channel,
        },
        filesInput: filesInputEnabled ? filesInputOptions : null,
        onError(errorMessage) {
          errorMessages = [...errorMessages, errorMessage];
        },
      },
      libraryUrls
    );
    vv.onArViewChanged = () => {
      arView = vv.arView;
    };
    vv.onSceneGrabbing = () => {
      cameraActionHintOpacity = 0;
      setTimeout(() => {
        viewerHintEnabled = false;
      }, 1000);
    };
    if (meshUrl) {
      await vv.importMeshAsync(meshUrl, { pluginExtension: '.glb', usdzUrl });
      await new Promise((resolve) => {
        vv.scene.onAfterRenderObservable.addOnce(resolve);
      });
    }
    vviewer = vv;
    thumbnailBackgroundColor = undefined;
    thumbnailUrl = undefined;
    loading = false;
  };

  const update = (options: VViewerOptions) => {
    vviewer?.update(options);
  };

  $: update({
    scene: {
      clearColor: finalRender ? (!skyboxDisabled ? '#ffffff' : sceneClearColor) : '#d9d9d9',
      environmentTexture: selectedEnvironmentTextureOption?.value,
      debugLayerVisiblity: debugLayer,
    },
    camera: {
      alpha: cameraAlpha,
      beta: cameraBeta,
      lowerAlphaLimit: cameraLowerAlphaLimit,
      upperAlphaLimit: cameraUpperAlphaLimit,
      lowerBetaLimit: cameraLowerBetaLimit,
      upperBetaLimit: cameraUpperBetaLimit,
      useAutoRotationBehavior: cameraAutoRotation,
    },
    light: {
      intensity: lightIntensity,
    },
    environment: finalRender
      ? {
          ...environmentOptions,
          createGround: groundEnabled,
          groundSize,
          groundColor,
          groundOpacity,
          enableGroundMirror: groundMirrorEnabled,
        }
      : null,
    skybox: !skyboxDisabled && finalRender ? skyboxDefaultOptions : null,
    grid: gridEnabled ? gridOptions : null,
    defaultPipeline: {
      imageProcessingEnabled: finalRender,
    },
    filesInput: filesInputEnabled ? filesInputOptions : null,
    meshRendering: {
      channel,
    },
  });

  onMount(() => {
    if (autoStart) prepare();
  });
</script>

<div class="vh-100 position-relative">
  {#if filesInputEnabled && filesInputMessage}
    <p class="position-absolute top-50 start-50 translate-middle">
      {filesInputMessage}
    </p>
  {/if}
  <canvas
    bind:this={canvasRef}
    class="renderCanvas position-absolute top-0 start-0 w-100 h-100"
    style:background-color={thumbnailBackgroundColor ?? ''}
    style:background-image={thumbnailUrl ? `url(${thumbnailUrl})` : ''}
    style:cursor={loading || vviewer ? null : 'pointer'}
    on:click={loading || vviewer ? null : prepare}
  />
  {#if bigButtonEnabled && !vviewer}
    <div
      class="bigButton position-absolute top-50 start-50 translate-middle"
      class:text-dark={colorScheme === 'light'}
      class:text-light={colorScheme === 'dark'}
    >
      {#if loading}
        <Fa icon={faSpinner} spin />
      {:else}
        <Fa icon={faCirclePlay} />
      {/if}
    </div>
  {/if}
  {#if viewerHintEnabled && vviewer}
    <div
      class="cameraActionHint position-absolute start-50 translate-middle text-white"
      style:opacity={cameraActionHintOpacity}
    >
      <span class="d-inline-block">
        <FaLayers size="2x">
          <FaLayersText scale={0.5} translateX={-0.1} translateY={-0.5}>
            <span
              class="d-block rounded-circle border border-secondary border-3 bg-white bg-opacity-50"
            />
          </FaLayersText>
          <Fa icon={faHandPointer} style="filter: drop-shadow(0 3px 5px #000)" />
        </FaLayers>
      </span>
    </div>
  {/if}
  {#if attributionMessage}
    <div
      class="position-absolute top-0 start-0 px-1 bg-opacity-75 small"
      class:text-bg-light={colorScheme === 'light'}
      class:text-bg-dark={colorScheme === 'dark'}
    >
      {attributionMessage}
    </div>
  {/if}
  {#if vviewer && inspectorOpened}
    <div
      class="inspector position-absolute top-0 end-0 h-100 d-flex flex-column flex-shrink-0 p-3 bg-opacity-75"
      class:text-bg-light={colorScheme === 'light'}
      class:text-bg-dark={colorScheme === 'dark'}
      style="width: 12em; padding-bottom: 4em !important; overflow-y: auto;"
    >
      <div class="h6">
        Inspector
        {#if versionVisibility}
          <span class="small opacity-25">v{version}</span>
        {/if}
      </div>
      {#if channelsEnabled}
        <div class="small">Render</div>
        <ul class="nav nav-pills flex-column mb-2">
          {#each ['finalRender'] as value}
            <li class="nav-item">
              <button
                class="text-link nav-link w-100 text-start"
                class:active={!channel || channel === value}
                on:click={() => {
                  // @ts-expect-error: channel is a const string
                  channel = value;
                }}
              >
                {humanize(value)}
              </button>
            </li>
          {/each}
        </ul>
        <div class="small">Material channels</div>
        <ul class="nav nav-pills flex-column mb-2">
          {#each ['baseColor', 'roughness', 'metallic', 'transmission'] as value}
            <li class="nav-item">
              <button
                class="text-link nav-link w-100 text-start"
                class:active={!channel || channel === value}
                on:click={() => {
                  // @ts-expect-error: channel is a const string
                  channel = value;
                }}
              >
                {humanize(value)}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
      <div class="small">Background</div>
      <ul class="nav nav-pills flex-column mb-2">
        {#each environmentTextureOptions as environmentTextureOption}
          <li class="nav-item">
            <button
              class="text-link nav-link w-100 text-start"
              class:active={!skyboxDisabled &&
                finalRender &&
                selectedEnvironmentTextureOption === environmentTextureOption}
              on:click={() => {
                selectedEnvironmentTextureOption = environmentTextureOption;
                channel = 'finalRender';
                skyboxDisabled = false;
              }}
            >
              {environmentTextureOption.label}
            </button>
          </li>
        {/each}
        <li class="nav-item">
          <span class="form-check mt-1">
            <input
              type="checkbox"
              class="form-check-input"
              style:margin-top="0.5em"
              bind:checked={skyboxDisabled}
            />
            <input
              type="color"
              class="form-control form-control-sm form-control-color"
              bind:value={sceneClearColor}
              on:input={() => {
                skyboxDisabled = true;
              }}
            />
          </span>
        </li>
      </ul>
      <div class="small">Additional Light</div>
      <ul class="nav nav-pills flex-column mb-2">
        <li class="nav-item">
          <input
            type="range"
            class="form-range"
            min="0"
            max="1"
            step="0.1"
            bind:value={lightIntensity}
          />
        </li>
      </ul>
      <div class="small">Helpers</div>
      <ul class="nav nav-pills flex-column mb-2">
        <li class="nav-item">
          <span class="form-check">
            <input
              type="checkbox"
              id="form-groundEnabled-check"
              class="form-check-input"
              bind:checked={groundEnabled}
              disabled={!finalRender}
            />
            <label for="form-groundEnabled-check" class="form-check-label">Ground</label>
          </span>
        </li>
        <li class="nav-item">
          <span class="form-check">
            <input
              type="checkbox"
              id="form-groundMirrorEnabled-check"
              class="form-check-input"
              bind:checked={groundMirrorEnabled}
              disabled={!groundEnabled || !finalRender}
            />
            <label for="form-groundMirrorEnabled-check" class="form-check-label">Mirror</label>
          </span>
        </li>
        <li class="nav-item">
          <span class="form-check">
            <input
              type="checkbox"
              id="form-gridEnabled-check"
              class="form-check-input"
              bind:checked={gridEnabled}
            />
            <label for="form-gridEnabled-check" class="form-check-label">Grid</label>
          </span>
        </li>
      </ul>
    </div>
  {/if}
  {#if controlsEnabled && !(bigButtonEnabled && !vviewer)}
    <nav
      class="navbar navbar-expand fixed-bottom bg-opacity-50"
      class:navbar-light={colorScheme === 'light'}
      class:text-bg-light={colorScheme === 'light'}
      class:navbar-dark={colorScheme === 'dark'}
      class:text-bg-dark={colorScheme === 'dark'}
    >
      <div class="container-fluid">
        {#if brandEnabled}
          <a
            class="navbar-brand"
            href="https://pfn3d.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              style="height:1em;line-height:.75em;font-size:1.33333em;vertical-align:-.225em;transform-origin:center;overflow:visible"
              viewBox="0 0 280 290"
            >
              <path
                fill="currentColor"
                d="M141.72,48.81c-4.19-24.73-25.71-43.63-51.61-43.63H23.98V161.04C4.66,177.03-.95,205.12,12,227.55l33.07,57.27,134.94-77.91c5.92,2.2,12.06,3.29,18.16,3.29,18.13,0,35.78-9.4,45.47-26.19l33.06-57.27L141.72,48.81Zm-88.94-14.83h37.33c13.02,0,23.6,10.59,23.6,23.6s-10.59,23.61-23.6,23.61H52.78V33.98Zm2.82,211.5l-18.66-32.33c-6.51-11.27-2.63-25.74,8.64-32.25,3.71-2.14,7.76-3.16,11.76-3.16,8.16,0,16.11,4.23,20.48,11.8l18.66,32.33-40.88,23.61Zm47.16-70.34c-7-12.12-18.3-20.79-31.82-24.42-6.01-1.61-12.14-2.08-18.16-1.55v-39.19h37.33c21,0,39.1-12.44,47.46-30.32l33.96,19.61-18.66,32.33c-7,12.12-8.86,26.25-5.24,39.77,1.61,6.01,4.27,11.55,7.74,16.51l-33.94,19.59-18.67-32.33Zm115.93-5.53c-3.15,5.46-8.24,9.37-14.33,11-6.09,1.63-12.45,.79-17.91-2.36-5.46-3.15-9.37-8.25-11-14.33-1.63-6.09-.79-12.45,2.36-17.91l18.67-32.33,40.88,23.6-18.66,32.33Z"
              />
            </svg>
          </a>
        {/if}
        <ul class="navbar-nav justify-content-end flex-grow-1">
          {#if loading}
            <li class="nav-item">
              <span id="act-loading" class="nav-link">
                <Fa icon={faSpinner} size="lg" spin />
              </span>
              {#if loadingMessage}
                <Tooltip target="act-loading">{loadingMessage}</Tooltip>
              {/if}
            </li>
          {:else if vviewer}
            {#if inspectorEnabled}
              <li class="nav-item">
                <button
                  id="act-inspector"
                  class="btn btn-link nav-link"
                  on:click={() => {
                    inspectorOpened = !inspectorOpened;
                  }}
                >
                  <Fa icon={faLayerGroup} size="lg" />
                </button>
                <Tooltip target="act-inspector">Inspector</Tooltip>
              </li>
            {/if}
            {#if arEnabled}
              <li class="nav-item">
                <button
                  id="act-start-ar"
                  class="btn btn-link nav-link"
                  class:disabled={arView == null}
                  on:click={arView}
                >
                  <FaLayers size="lg">
                    <Fa icon={faCube} />
                    <FaLayersText
                      scale={0.5}
                      translateY={-0.05}
                      color="white"
                      style="font-weight: 900"
                    >
                      AR
                    </FaLayersText>
                  </FaLayers>
                </button>
                <Tooltip target="act-start-ar">
                  {#if arView != null}
                    View in AR
                  {:else}
                    AR not available
                  {/if}
                </Tooltip>
              </li>
            {/if}
            {#if filesInputEnabled}
              <li class="nav-item">
                <input
                  bind:this={fileRef}
                  type="file"
                  class="d-none"
                  on:change={(evt) => {
                    vviewer?.filesInput?.loadFiles(evt);
                  }}
                />
                <button
                  id="act-open-file"
                  class="btn btn-link nav-link"
                  on:click={() => {
                    fileRef.click();
                  }}
                >
                  <Fa icon={faFileArrowUp} size="lg" />
                </button>
                <Tooltip target="act-open-file">Open mesh file</Tooltip>
              </li>
            {/if}
          {:else}
            <li class="nav-item">
              <button id="act-start-3d" class="btn btn-link nav-link" on:click={prepare}>
                <Fa icon={faCirclePlay} size="lg" />
              </button>
              <Tooltip target="act-start-3d">Start 3D!</Tooltip>
            </li>
          {/if}
        </ul>
      </div>
    </nav>
  {/if}
  {#if errorMessagesVisibility}
    <div class="position-absolute top-0 start-0 w-100">
      {#each errorMessages as errorMessage}
        <Alert color="danger" class="m-2" dismissible>{errorMessage}</Alert>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  .renderCanvas {
    -webkit-tap-highlight-color: transparent;
    touch-action: none;
    outline: none;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }

  .bigButton {
    pointer-events: none;
    font-size: 20vmin;
  }

  .cameraActionHint {
    pointer-events: none;
    top: 60%;
    transition: opacity 0.5s;

    .d-inline-block {
      animation: move-left-and-right 4s ease-in-out infinite;

      .d-block.border {
        width: 1em;
        height: 1em;
      }
    }
  }

  .inspector.text-bg-dark {
    .small {
      // .text-white-50
      color: rgba(255, 255, 255, 0.5) !important;
    }

    .text-link {
      // .text-white
      color: rgba(255, 255, 255, 1) !important;
    }
  }

  .navbar {
    pointer-events: none;
    z-index: auto;

    a,
    button {
      pointer-events: auto;
    }
  }

  .btn {
    &:disabled,
    &.disabled {
      color: var(--bs-btn-disabled-color) !important;
    }
  }

  .nav-link.btn-link {
    --bs-btn-border-width: 0;
  }

  @keyframes move-left-and-right {
    0% {
      transform: translateX(-2em);
    }
    50% {
      transform: translateX(2em);
    }
    100% {
      transform: translateX(-2em);
    }
  }
</style>
